import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { ArrowLeft, PanelRight, Loader2, MessageCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Bubble, DaySeparator, DeleteButton } from "@/components/chat/MessageList";
import { ParticipantDetails } from "./ParticipantDetails";
import { Classification } from "./Classification";
import { StatusPill } from "./ui";
import { errMsg } from "@/lib/api";
import { fmtTime, fmtDateTime, maskPhone, waLink } from "@/lib/format";
import { ReplyBox, waReplyText } from "./ReplyBox";

export { waReplyText };

const WA_LABEL = { preparada: "Preparada para WhatsApp", enviado: "Resposta externa confirmada", nao_enviado: "WhatsApp: não enviado", retornar_depois: "WhatsApp: retornar depois" };

const WaControls = ({ msg, p, onWaStatus }) => {
  if (msg.channel !== "whatsapp") return <span data-testid={`msg-internal-${msg.id}`} className="mr-auto text-zap-ink/60">Resposta interna</span>;
  const last = (msg.wa_log || []).slice(-1)[0];
  return (
    <span className="mr-auto flex flex-col items-start gap-1">
      <span data-testid={`msg-wa-label-${msg.id}`} className={`flex items-center gap-1 font-semibold ${msg.wa_status === "enviado" ? "text-emerald-800" : "text-[#7A5A14]"}`}>
        {msg.wa_status === "enviado" && <CheckCircle2 className="h-3.5 w-3.5" />}{WA_LABEL[msg.wa_status]}{last ? ` · ${fmtDateTime(last.at)}` : ""}{msg.from_number ? ` · ${msg.from_number}` : ""}
      </span>
      {msg.wa_status !== "enviado" && (
        <span className="flex flex-wrap gap-1">
          <a data-testid={`msg-wa-open-${msg.id}`} href={waLink(p.whatsapp, waReplyText(p, msg.text))} target="_blank" rel="noreferrer" className="rounded border border-zap-green/40 px-1.5 py-0.5 font-semibold text-zap-green">Abrir WhatsApp</a>
          <button data-testid={`msg-wa-sent-btn-${msg.id}`} onClick={() => onWaStatus(msg.id, "enviado")} className="rounded bg-zap-deep px-1.5 py-0.5 font-semibold text-white">ENVIO REALIZADO</button>
          <button data-testid={`msg-wa-notsent-${msg.id}`} onClick={() => onWaStatus(msg.id, "nao_enviado")} className="rounded border border-zap-line px-1.5 py-0.5 font-semibold">NÃO ENVIADO</button>
          <button data-testid={`msg-wa-later-${msg.id}`} onClick={() => onWaStatus(msg.id, "retornar_depois")} className="rounded border border-zap-line px-1.5 py-0.5 font-semibold">RETORNAR DEPOIS</button>
        </span>
      )}
    </span>
  );
};

const Thread = ({ data, api, onPatch, onWaStatus, onReclassify, onDelete }) => {
  const end = useRef(null);
  useEffect(() => {
    end.current?.scrollIntoView({ block: "end" });
    const t = setTimeout(() => end.current?.scrollIntoView({ block: "end" }), 600);
    return () => clearTimeout(t);
  }, [data.messages.length]);
  const p = data.participant;
  return (
    <div data-testid="admin-thread" className="py-2">
      {data.messages.map((m, i) => (
        <Fragment key={m.id}>
          {(i === 0 || new Date(m.created_at).toDateString() !== new Date(data.messages[i - 1].created_at).toDateString()) && <DaySeparator iso={m.created_at} />}
          {m.sender === "system" ? (
            <p className="my-2 text-center text-xs text-muted-foreground">Mensagem automática · {fmtTime(m.created_at)}</p>
          ) : (
            <Bubble msg={m} mine={m.sender === "staff"} client={api} base="/admin/files"
              footer={<>{m.sender === "participant" && !m.deleted_at && <DeleteButton msg={m} onDelete={onDelete} label="Remover" />}{m.deleted_at && <span className="mr-1">apagada por {m.deleted_by}</span>}{m.hidden_for_participant_at && <span data-testid={`msg-hidden-${m.id}`} className="mr-1">apagada pelo participante só para ele</span>}{m.sender === "staff" && <WaControls msg={m} p={p} onWaStatus={onWaStatus} />}{m.receipt_status && <StatusPill status={m.receipt_status} />}<span title={fmtDateTime(m.created_at)}>{fmtTime(m.created_at)}</span></>}>
              {m.sender === "participant" ? (!m.deleted_at && <Classification msg={m} onPatch={onPatch} onReclassify={onReclassify} />) : <p className="mb-0.5 text-sm font-semibold text-zap-green">{m.staff_name || "Equipe"}{m.channel === "whatsapp" && <span className="ml-2 text-xs font-medium text-muted-foreground">(não aparece no site)</span>}{m.broadcast_name && <span data-testid={`msg-broadcast-${m.id}`} className="ml-2 text-xs font-medium text-muted-foreground">Transmissão: {m.broadcast_name}</span>}</p>}
            </Bubble>
          )}
        </Fragment>
      ))}
      <div ref={end} />
    </div>
  );
};

export function Conversation({ pid, onBack, onChanged }) {
  const { api } = useOutletContext();
  const [data, setData] = useState(null);
  const [details, setDetails] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/participants/${pid}`);
      setData(r.data);
      if (r.data.participant.unread_count > 0 || r.data.participant.status === "recebido") {
        await api.post(`/admin/participants/${pid}/read`);
        setData((await api.get(`/admin/participants/${pid}`)).data);
        onChanged();
      }
    } catch {
      setData(false);
    }
  }, [api, pid, onChanged]);
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const patchMsg = async (id, body) => {
    try { await api.patch(`/admin/messages/${id}`, body); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const reclassify = async (id) => {
    try { await api.post(`/admin/messages/${id}/reclassify`); await load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const setWaStatus = async (id, status) => {
    const msgs = { enviado: "Envio registrado.", nao_enviado: "Registrado como não enviado.", retornar_depois: "Marcado para retornar depois." };
    try { await api.post(`/admin/messages/${id}/whatsapp-status`, { status }); load(); onChanged(); toast.success(msgs[status]); } catch (e) { toast.error(errMsg(e)); }
  };
  const refresh = () => { load(); onChanged(); };
  const deleted = () => { onChanged(); onBack(); };

  if (data === false) return <p className="p-8">Conversa não encontrada.</p>;
  if (!data) return <div className="flex flex-1 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-zap-deep" /></div>;
  const p = data.participant;
  return (
    <div className="flex min-w-0 flex-1">
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-16 shrink-0 items-center gap-2 border-b border-zap-line bg-white px-3">
          <button data-testid="conversation-back" onClick={onBack} className="flex h-10 w-10 items-center justify-center rounded-md hover:bg-zap-mist lg:hidden"><ArrowLeft className="h-5 w-5" /></button>
          <div className="min-w-0 flex-1">
            <p data-testid="conversation-name" className="truncate font-semibold text-zap-ink">{p.name}</p>
            <p className="flex min-w-0 items-center gap-2 truncate text-sm text-muted-foreground"><MessageCircle className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{maskPhone(p.whatsapp)} · {p.city}/{p.state}</span></p>
          </div>
          <span className="hidden sm:inline-flex"><StatusPill status={p.status} /></span>
          <button data-testid="conversation-details-toggle" onClick={() => setDetails(true)} className="flex h-10 w-10 items-center justify-center rounded-md hover:bg-zap-mist 2xl:hidden" aria-label="Detalhes"><PanelRight className="h-5 w-5" /></button>
        </div>
        <div className="chat-canvas scroll-thin min-h-0 flex-1 overflow-y-auto"><Thread data={data} api={api} onPatch={patchMsg} onWaStatus={setWaStatus} onReclassify={reclassify}
          onDelete={async (id) => { try { await api.delete(`/admin/messages/${id}`); toast.success("Mensagem removida."); load(); } catch (e) { toast.error(errMsg(e)); } }} /></div>
        <ReplyBox api={api} p={p} onSent={refresh} />
      </div>
      <aside className="scroll-thin hidden w-[300px] shrink-0 overflow-y-auto border-l border-zap-line bg-white 2xl:block">
        <ParticipantDetails p={p} messages={data.messages} onChanged={refresh} onDeleted={deleted} />
      </aside>
      <Sheet open={details} onOpenChange={setDetails}>
        <SheetContent className="w-[340px] overflow-y-auto p-0">
          <SheetHeader className="px-5 pt-5"><SheetTitle>Participante</SheetTitle></SheetHeader>
          <ParticipantDetails p={p} messages={data.messages} onChanged={refresh} onDeleted={deleted} />
        </SheetContent>
      </Sheet>
    </div>
  );
}
